import { formatInTimeZone } from 'date-fns-tz';

import { OUTREACH_ANALYTICS_TIMEZONE } from '@apps/web/components/admin/outreach_observability/constants';

// A value plotted against an ET hour-of-day (0–23) axis, so a partial "today"
// series and an immutable comparison day line up hour-for-hour regardless of
// their calendar date. A null value breaks the line rather than dropping to a
// misleading zero.
export type HourOfDayPoint = {
    readonly hourOfDay: number;
    readonly value: number | null;
};

function etHourOfDay(bucketStart: string, timeZone: string): number {
    return Number(formatInTimeZone(new Date(bucketStart), timeZone, 'H'));
}

// Converts UTC `bucket_start` ISO strings into ET hour-of-day points. Designed
// so a later wave can feed HourlyKpiBucket or ComparisonHourBucket directly by
// passing the matching value accessor. `timeZone` defaults to the shared
// outreach analytics timezone but stays overridable for testing.
export function toHourOfDaySeries<T extends { readonly bucket_start: string }>(
    buckets: Array<T>,
    getValue: (bucket: T) => number | null,
    timeZone: string = OUTREACH_ANALYTICS_TIMEZONE,
): Array<HourOfDayPoint> {
    return buckets.map((bucket) => ({
        hourOfDay: etHourOfDay(bucket.bucket_start, timeZone),
        value: getValue(bucket),
    }));
}

// Sum of `field` over the buckets whose ET hour-of-day is at or before
// `nowEtHour` — the "compare to the same time of day" primitive that keeps a
// partial-day today honest against a full comparison day.
export function cumulativeUpToSameHour<
    T extends { readonly bucket_start: string },
>(hourly: Array<T>, field: (bucket: T) => number, nowEtHour: number): number {
    let total = 0;
    for (const bucket of hourly) {
        if (
            etHourOfDay(bucket.bucket_start, OUTREACH_ANALYTICS_TIMEZONE) <=
            nowEtHour
        ) {
            total += field(bucket);
        }
    }
    return total;
}

// Percent change of today vs a comparison value. Null when the comparison base
// is 0, since there is no meaningful ratio against nothing.
export function sameTimeDeltaPct(
    todayValue: number,
    comparisonValue: number,
): number | null {
    if (comparisonValue === 0) {
        return null;
    }
    return ((todayValue - comparisonValue) / comparisonValue) * 100;
}

// Direction quality of a signed delta. A zero delta (including one that
// rounds to zero for display) is neutral — never an improvement or a
// regression in either direction.
export type DeltaTone = 'good' | 'bad' | 'neutral';

export function deltaTone(delta: number, goodWhenUp: boolean): DeltaTone {
    if (delta === 0) {
        return 'neutral';
    }
    return delta > 0 === goodWhenUp ? 'good' : 'bad';
}
